/**
 * Mobile Bottom Panel Component
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Bottom panel for selecting accounts, currencies, and categories
 */

import { Box, Divider, List, ListItem, ListItemButton, ListItemText, Paper, Typography } from "@mui/material";
import { Account, Currency, TransactionCategory, TransactionCategoryGroup } from "../../../../types";

export interface MobileBottomPanelProps {
  open: boolean;
  focusedField: "account" | "fromAccount" | "toAccount" | "currency" | "category" | "feeAccount" | "feeAmount" | `detail:${string}` | null;
  // Account selection
  accounts: Account[];
  selectedAccountId?: string;
  selectedFromAccountId?: string;
  selectedToAccountId?: string;
  selectedFeeAccountId?: string;
  onAccountSelect: (accountId: string) => void;
  // Currency selection
  currencies: Currency[];
  defaultCurrency: string;
  selectedCurrency?: string;
  onCurrencySelect: (currencyCode: string) => void;
  // Category selection
  categories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  selectedCategory?: string;
  onCategorySelect: (categoryName: string) => void;
  // Formatting
  formatCurrency: (amount: number, currency: string) => string;
}

export default function MobileBottomPanel({
  open,
  focusedField,
  accounts,
  selectedAccountId,
  selectedFromAccountId,
  selectedToAccountId,
  selectedFeeAccountId,
  onAccountSelect,
  currencies,
  defaultCurrency,
  selectedCurrency,
  onCurrencySelect,
  categories,
  categoryGroups,
  selectedCategory,
  onCategorySelect,
  formatCurrency,
}: MobileBottomPanelProps) {
  if (!open || !focusedField) return null;

  // Get filtered accounts for selection
  const getFilteredAccounts = (): Account[] => {
    if (focusedField === "toAccount" && selectedFromAccountId) {
      return accounts.filter((acc) => acc.id !== selectedFromAccountId);
    }
    return accounts;
  };

  // Get group name for category
  const getGroupName = (category: TransactionCategory): string => {
    if (!category.groupId) return "Ungrouped";
    const group = categoryGroups.find((g) => g.id === category.groupId);
    return group?.name || "Ungrouped";
  };

  const getTitle = () => {
    switch (focusedField) {
      case "account":
        return "Select Account";
      case "fromAccount":
        return "Select From Account";
      case "toAccount":
        return "Select To Account";
      case "feeAccount":
        return "Select Fee Account";
      case "currency":
        return "Select Currency";
      case "category":
        return "Select Category";
      default:
        return "";
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: "50vh",
        maxHeight: "50vh",
        bgcolor: "background.paper",
        border: "none",
        borderTop: "2px solid",
        borderColor: "primary.main",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px",
        boxShadow: (theme) => theme.palette.mode === "dark" ? "0 -4px 20px rgba(0, 0, 0, 0.4)" : "0 -4px 20px rgba(0, 0, 0, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1)",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        pointerEvents: "auto",
        WebkitTransform: "translate3d(0, 0, 0)",
        transform: "translate3d(0, 0, 0)",
      }}
      onMouseDown={(e) => {
        // Prevent closing when clicking inside the panel
        e.stopPropagation();
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {getTitle()}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        {/* Account List */}
        {(focusedField === "account" || focusedField === "fromAccount" || focusedField === "toAccount" || focusedField === "feeAccount") && (
          <List sx={{ p: 0 }}>
            {getFilteredAccounts()
              .sort((a, b) => {
                if (a.group !== b.group) {
                  return a.group.localeCompare(b.group);
                }
                return a.accountName.localeCompare(b.accountName);
              })
              .map((account, index, arr) => {
                const showGroupHeader = index === 0 || arr[index - 1].group !== account.group;
                const isSelected =
                  (focusedField === "account" && selectedAccountId === account.id) ||
                  (focusedField === "fromAccount" && selectedFromAccountId === account.id) ||
                  (focusedField === "toAccount" && selectedToAccountId === account.id) ||
                  (focusedField === "feeAccount" && selectedFeeAccountId === account.id);

                return (
                  <Box key={account.id}>
                    {showGroupHeader && (
                      <>
                        {index > 0 && <Divider />}
                        <Box sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {account.group}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => onAccountSelect(account.id)} selected={isSelected}>
                        <ListItemText
                          primary={account.accountName}
                          secondary={`${formatCurrency(account.amount || 0, account.currency)} (${account.currency})`}
                        />
                      </ListItemButton>
                    </ListItem>
                  </Box>
                );
              })}
          </List>
        )}

        {/* Currency List */}
        {focusedField === "currency" && (
          <List sx={{ p: 0 }}>
            {/* Include default currency first */}
            <ListItem key={defaultCurrency} disablePadding>
              <ListItemButton
                onClick={() => onCurrencySelect(defaultCurrency)}
                selected={selectedCurrency === defaultCurrency || (!selectedCurrency && false)}
              >
                <ListItemText primary={defaultCurrency} secondary="Default currency" />
              </ListItemButton>
            </ListItem>
            {/* Include all currencies from settings (excluding default currency) */}
            {currencies
              .filter((c) => c.code && c.code !== defaultCurrency)
              .map((currency) => {
                const currencyCode = currency.code || "";
                const currencyName = currency.name || "";
                const currencySymbol = currency.symbol || "";

                if (!currencyCode) {
                  return null;
                }

                const displayText = currencyName ? `${currencyCode} - ${currencyName}` : currencyCode;

                return (
                  <ListItem key={currencyCode} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        onCurrencySelect(currencyCode);
                      }}
                      selected={selectedCurrency === currencyCode}
                    >
                      <ListItemText primary={displayText} secondary={currencySymbol || undefined} />
                    </ListItemButton>
                  </ListItem>
                );
              })
              .filter(Boolean)}
          </List>
        )}

        {/* Category List */}
        {focusedField === "category" && (
          <List sx={{ p: 0 }}>
            {categories
              .sort((a, b) => {
                const groupA = getGroupName(a);
                const groupB = getGroupName(b);
                if (groupA !== groupB) {
                  return groupA.localeCompare(groupB);
                }
                return a.name.localeCompare(b.name);
              })
              .map((category, index, arr) => {
                const groupName = getGroupName(category);
                const showGroupHeader = index === 0 || getGroupName(arr[index - 1]) !== groupName;

                return (
                  <Box key={category.id}>
                    {showGroupHeader && (
                      <>
                        {index > 0 && <Divider />}
                        <Box sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {groupName}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onCategorySelect(category.name);
                        }}
                        selected={selectedCategory === category.name}
                      >
                        <ListItemText primary={category.name} />
                      </ListItemButton>
                    </ListItem>
                  </Box>
                );
              })}
          </List>
        )}
      </Box>
    </Paper>
  );
}
